import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const DeliveryScheduler = ({ packages, onScheduleUpdate }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('morning');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(true);
  const [leaveAtDoor, setLeaveAtDoor] = useState(false);

  const timeSlots = [
    { value: 'morning', label: 'Matin (8h - 12h)', icon: 'Sunrise' },
    { value: 'afternoon', label: 'Après-midi (12h - 17h)', icon: 'Sun' },
    { value: 'evening', label: 'Soir (17h - 20h)', icon: 'Sunset' }
  ];

  const handleScheduleSubmit = (e) => {
    e?.preventDefault();
    
    const scheduleData = {
      packageId: selectedPackage?.id,
      deliveryDate,
      deliveryTime,
      specialInstructions,
      requiresSignature,
      leaveAtDoor
    };

    console.log('Schedule data:', scheduleData);
    // Call API to update delivery schedule
    onScheduleUpdate?.();
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.split('T')?.[0];
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Package Selection */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sélectionner un colis</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {packages?.filter(pkg => pkg?.status !== 'delivered')?.map(pkg => (
              <button
                key={pkg?.id}
                onClick={() => setSelectedPackage(pkg)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                  selectedPackage?.id === pkg?.id
                    ? 'border-primary bg-primary/5' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{pkg?.packageNumber}</div>
                <div className="text-sm text-gray-600">{pkg?.patientName}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Livraison: {new Date(pkg?.estimatedDelivery)?.toLocaleDateString('fr-FR')}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Scheduling Form */}
      <div className="lg:col-span-2">
        {selectedPackage ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Planifier la livraison</h3>

            <form onSubmit={handleScheduleSubmit} className="space-y-6">
              {/* Package Info */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Package" size={20} className="text-primary" />
                  <span className="font-semibold text-gray-900">{selectedPackage?.packageNumber}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Livraison estimée: {new Date(selectedPackage?.estimatedDelivery)?.toLocaleDateString('fr-FR')}
                </div>
              </div>

              {/* Date Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Date de livraison préférée *
                </label>
                <Input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e?.target?.value)}
                  min={getMinDate()}
                  required
                />
              </div>

              {/* Time Slot Selection */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  Créneau horaire *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {timeSlots?.map(slot => (
                    <button
                      key={slot?.value}
                      type="button"
                      onClick={() => setDeliveryTime(slot?.value)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        deliveryTime === slot?.value
                          ? 'border-primary bg-primary/5' :'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon name={slot?.icon} size={24} className="mx-auto mb-2" />
                      <div className="text-sm font-medium text-gray-900">{slot?.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Delivery Options */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-3 block">
                  Options de livraison
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={requiresSignature}
                      onChange={(e) => setRequiresSignature(e?.target?.checked)}
                      className="w-5 h-5 text-primary rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Signature requise</div>
                      <div className="text-sm text-gray-600">Le livreur devra obtenir une signature</div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={leaveAtDoor}
                      onChange={(e) => setLeaveAtDoor(e?.target?.checked)}
                      className="w-5 h-5 text-primary rounded focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Laisser à la porte</div>
                      <div className="text-sm text-gray-600">Autoriser le dépôt sans signature</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Special Instructions */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Instructions spéciales (optionnel)
                </label>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e?.target?.value)}
                  placeholder="Ex: Sonner à l'interphone, code d'accès, instructions d'accès..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  iconName="X"
                  onClick={() => setSelectedPackage(null)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  iconName="Check"
                  className="flex-1"
                  disabled={!deliveryDate}
                >
                  Confirmer la planification
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Icon name="Calendar" size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionnez un colis</h3>
            <p className="text-sm text-gray-600">
              Choisissez un colis dans la liste pour planifier sa livraison
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryScheduler;